/**
 * The asset manifest schema (M1 asset contract §2.3). Gameplay-owned, shape FROZEN for M1:
 * after the lane fork only additive optional fields may be added (through the merge agent).
 * Imports only `zod`, so Node tools can import it directly with type stripping.
 */
import { z } from 'zod'

export const ASSET_ID = /^[a-z]+(?:\.[a-z0-9_]+)+$/
export const ASSET_PATH = /^\/assets\/[a-z0-9_]+\/[a-z0-9_.]+\.[0-9a-f]{8}\.glb$/
/** `BADGE`: a badge station on a baked (Meshy) hull (D-TBD, Meshy-ships plan C5): out = surface normal, params w, h, kind. */
export const SOCKET_KINDS = ['HP', 'ENG', 'EYE', 'DOCK', 'CON', 'LIGHT', 'BADGE'] as const
export const SOCKET_NAME = /^(HP|ENG|EYE|DOCK|CON|LIGHT|BADGE)_[A-Z0-9]+(?:_[A-Z0-9]+)*$/
export const COLLIDER_NAME = /^COL_[A-Z0-9]+(?:_[A-Z0-9]+)*$/
/**
 * `DCL_<station>`: a conformed decal patch mesh at the root of a baked hull (D-TBD, Meshy-ships plan C6): MAT_decal,
 * LOD0 only, paired with the `BADGE_<station>` socket of the same suffix.
 */
export const DECAL_PATCH_NAME = /^DCL_[A-Z0-9]+(?:_[A-Z0-9]+)*$/
/** What a BADGE_ station carries (its `kind` param): a DCL_ decal patch, a trim part, or both. */
export const BADGE_KINDS = ['decal', 'part', 'both'] as const
export const LOD_NAME = /^LOD[0-3]$/
export const MATERIAL_SLOTS = ['hull', 'glass', 'emissive', 'decal', 'set'] as const
export const MATERIAL_NAME = /^MAT_(hull|glass|emissive|decal|set)$/
export const TEXTURE_MAPS = ['baseColor', 'normal', 'orm', 'emissive'] as const
/** Values of a LIGHT_ socket's `type` param. `sensor` = sensor eyes (red raider eyes), added by the M1 architect. */
export const LIGHT_TYPES = ['nav', 'strobe', 'work', 'sensor'] as const

const vec3 = z.tuple([z.number(), z.number(), z.number()])
const quat = z.tuple([z.number(), z.number(), z.number(), z.number()]) // x, y, z, w
const positive = z.number().positive()

// M2 (plan 2026-09-27 §2.10, D-TBD): `station` = single-GLB stations with a DOCK_BAY_ socket; `prop` = loose salvage components and ore chunks.
/** `structure` and `poi_prop`: the M3 Reach placeholders (relays, lane rings, stations, bases; beacons, caches, anomalies). */
export const assetClassSchema = z.enum([
  'ship',
  'hardpoint',
  'ordnance',
  'station_module',
  'cockpit',
  'texture_set',
  'station',
  'prop',
  'structure',
  'poi_prop',
  'trim_part', // code-built badge and grade parts mounted at BADGE_/CON_ sockets (D-TBD, Meshy-ships plan §3.2)
])
export const budgetClassSchema = z.enum([
  'ship_player',
  'ship_light',
  'ship_medium', // bounds radius 8-40 m; must clear the M bay (bayFit, M10 P1c1)
  'ship_large', // L hulls, radius 35-150 m, moor only (M10 P1c1); at most 4 battery mounts (MAX_TURRET_MOUNTS)
  'ship_capital', // capitals, radius 150-600 m, asset class station (M10 P1c1)
  'hardpoint',
  'ordnance',
  'station_module',
  'cockpit_frame',
  'material_set',
  'detail_set',
  'decal_atlas',
  'vfx_atlas',
  'post_set',
  'station',
  'prop',
  'structure',
  'poi_prop',
  // Baked-texture twins of the ship bands (D-TBD, Meshy-ships plan C1, §1.2): the band's triangle caps and LOD
  // distances, a 512² far set (base colour, normal, ORM) in the GLB; ship_maps is the streamed 2K near-set carrier.
  'ship_player_baked',
  'ship_light_baked',
  'ship_medium_baked',
  'ship_large_baked',
  'ship_maps',
  'trim_part',
])
export const socketKindSchema = z.enum(SOCKET_KINDS)

export const socketSchema = z
  .object({
    name: z.string().regex(SOCKET_NAME),
    kind: socketKindSchema,
    position: vec3, // metres, asset space
    quaternion: quat, // socket local −Z = "out" direction, local +Y = up
    params: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])),
  })
  .refine((s) => s.name.startsWith(`${s.kind}_`), { message: 'socket kind must match its name prefix' })

export const colliderSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ball'), radius: positive }),
  z.object({
    kind: z.literal('convexHulls'),
    nodes: z.array(z.string().regex(COLLIDER_NAME)).min(1), // COL_* node names, in GLB order
    hullVerts: z.array(z.number().int().min(4)).min(1), // hull vertex count per node, same order
  }),
  z.object({
    kind: z.literal('trimesh'),
    nodes: z.array(z.string().regex(COLLIDER_NAME)).min(1),
    triangles: z.number().int().positive(),
  }),
])

export const lodSchema = z
  .object({
    source: z.enum(['authored', 'gltfpack', 'none']),
    distances: z.array(z.number().nonnegative()).min(1).max(4), // switch-in distance per level, metres
    hysteresis: z.number().min(0).max(0.5),
    tris: z.array(z.number().int().nonnegative()).min(1).max(4), // measured per level by the pipeline
  })
  .refine((l) => l.distances[0] === 0, { message: 'LOD0 starts at 0 m' })
  .refine((l) => l.distances.every((d, i) => i === 0 || d > l.distances[i - 1]), { message: 'distances must increase' })
  .refine((l) => l.tris.length === l.distances.length, { message: 'one tri count per LOD' })

export const boundsSchema = z.object({ min: vec3, max: vec3, center: vec3, radius: positive })

export const provenanceSummarySchema = z
  .object({
    source: z.enum([
      'blender-script',
      'blend-file',
      'blender-bake',
      'image-gen',
      'procedural-ts',
      'greybox',
      'cc0-pack',
      'cc-by-pack',
      'meshy',
    ]),
    tool: z.string().min(1), // e.g. "Blender 5.2.0 + assets-src/ship.kestrel/build.py @ <git sha>"
    humanContribution: z.string().min(1), // one-line summary; "none" is a valid value
    license: z.enum(['LicenseRef-Eclipse-Owned', 'CC0-1.0', 'CC-BY-4.0', 'CC-BY-3.0']),
    attribution: z.string().min(1).optional(),
    aiGenerated: z.boolean(), // true when Claude or an AI model generated the form (Steam: Pre-Generated AI)
    manifestRef: z.string().optional(), // key in assets-src/manifest.json; absent for placeholders
  })
  .refine((p) => !p.license.startsWith('CC-BY') || p.attribution !== undefined, { message: 'CC-BY needs attribution' })

const common = {
  id: z.string().regex(ASSET_ID),
  class: assetClassSchema,
  budget: budgetClassSchema,
  path: z.string().regex(ASSET_PATH),
  bytes: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  ledgerLine: z.string().min(1),
  placeholder: z.boolean(),
  provenance: provenanceSummarySchema,
}

export const modelEntrySchema = z.object({
  ...common,
  kind: z.literal('model'),
  materialSlots: z.array(z.enum(MATERIAL_SLOTS)).min(1),
  lod: lodSchema,
  sockets: z.array(socketSchema),
  collider: colliderSchema.nullable(), // null: never collides (hardpoints, ordnance, cockpit frame)
  bounds: boundsSchema,
})

export const textureSetEntrySchema = z.object({
  ...common,
  kind: z.literal('textureSet'),
  maps: z.array(z.enum(TEXTURE_MAPS)).min(1),
  size: z.tuple([z.number().int().positive(), z.number().int().positive()]), // largest map, px
  texelsPerMeter: positive.optional(), // trim and detail sets
})

export const assetsFileSchema = z
  .object({ version: z.literal(1), models: z.array(modelEntrySchema), textureSets: z.array(textureSetEntrySchema) })
  .superRefine((file, ctx) => {
    const all = [...file.models, ...file.textureSets]
    const ids = new Set<string>()
    for (const e of all) {
      if (ids.has(e.id)) ctx.addIssue({ code: 'custom', message: `duplicate id ${e.id}` })
      ids.add(e.id)
      if (e.placeholder !== e.path.startsWith('/assets/placeholder/'))
        ctx.addIssue({ code: 'custom', message: `${e.id}: placeholder flag and path disagree` })
      if (!e.path.includes(`/${e.id}.`)) ctx.addIssue({ code: 'custom', message: `${e.id}: file name must start with the id` })
    }
  })

export type AssetsFile = z.infer<typeof assetsFileSchema>
export type ModelEntry = z.infer<typeof modelEntrySchema>
export type TextureSetEntry = z.infer<typeof textureSetEntrySchema>
export type SocketEntry = z.infer<typeof socketSchema>
export type ColliderEntry = z.infer<typeof colliderSchema>
export type MaterialSlot = (typeof MATERIAL_SLOTS)[number]
export type SocketKind = (typeof SOCKET_KINDS)[number]
export type TextureMap = (typeof TEXTURE_MAPS)[number]
export type BadgeKind = (typeof BADGE_KINDS)[number]
